'use client';

import type { FunctionComponent, ReactNode } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

type Size = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const sizeClasses: Record<Size, string> = {
  sm: 'max-w-[460px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
  xl: 'max-w-[92vw] w-[640px]',
  '2xl': 'max-w-[840px]',
};

interface Props {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  size?: Size;
  className?: string;
  closable?: boolean;
  children: ReactNode;
}

export const Modal: FunctionComponent<Props> = ({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  className,
  closable = true,
  children,
}) => {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && closable) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, closable]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto p-[24px]"
      style={{ background: 'rgba(15, 17, 21, 0.45)' }}
      onClick={closable ? onClose : undefined}
    >
      <div
        className={[
          'bg-bg-2 border border-line-2 rounded-[var(--radius-lg)] w-full flex flex-col',
          'shadow-[0_20px_60px_rgba(0,0,0,0.18)]',
          sizeClasses[size],
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={e => e.stopPropagation()}
      >
        {title !== undefined ? (
          <div className="flex items-center justify-between p-[16px_20px] border-b border-line">
            <div>
              <div className="font-sans text-[16px] font-semibold tracking-[-0.015em] text-ink">
                {title}
              </div>
              {subtitle ? <div className="text-[11.5px] text-ink-3 mt-[2px]">{subtitle}</div> : null}
            </div>
            {closable ? (
              <button
                type="button"
                className="bg-transparent border-0 text-[22px] leading-none text-ink-4 cursor-pointer px-[4px] transition-colors duration-150 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            ) : null}
          </div>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  );
};
